import { signOut } from "next-auth/react";

import { PageTypeAdmin, PageTypePortal } from "@/lib/types";
import { buildAbsoluteAppUrl, getAuthOriginForPath } from "./app-origin";

export const AUTH_REAUTH_QUERY_PARAM = "reauth";

let isHandlingAuthFailure = false;

export function getLoginUrl(pathname: string): string {
  const isAdminRoute = pathname.startsWith("/admin");
  return isAdminRoute ? PageTypeAdmin.SignIn : PageTypePortal.Login;
}

export function buildLoginRedirectUrl(
  pathname: string,
  options?: {
    forceReauth?: boolean;
    search?: string;
    origin?: string;
    hostname?: string;
  },
): string {
  const loginPath = getLoginUrl(pathname);
  const authOrigin = getAuthOriginForPath(
    loginPath,
    options?.hostname,
    options?.origin,
  );
  const callbackUrl = buildAbsoluteAppUrl(pathname, {
    search: options?.search,
    hostname: options?.hostname,
    origin: options?.origin,
  });

  const params = new URLSearchParams({
    callbackUrl,
  });

  if (options?.forceReauth) {
    params.set(AUTH_REAUTH_QUERY_PARAM, "1");
  }

  const query = params.toString();
  if (authOrigin) {
    return `${authOrigin.replace(/\/$/, "")}${loginPath}?${query}`;
  }

  return `${loginPath}?${query}`;
}

export function shouldSkipAuthenticatedLanding(
  reauth: string | null | undefined,
): boolean {
  return reauth === "1";
}

/**
 * Clears the NextAuth session before redirecting to login so auth pages do not
 * immediately bounce authenticated users back to the dashboard (401 redirect loop).
 */
export async function redirectToLoginAfterAuthFailure(
  pathname: string,
): Promise<void> {
  if (typeof window === "undefined") return;
  if (isHandlingAuthFailure) return;

  isHandlingAuthFailure = true;

  const destination = buildLoginRedirectUrl(pathname, {
    forceReauth: true,
    search: window.location.search,
    origin: window.location.origin,
    hostname: window.location.hostname,
  });

  try {
    await signOut({ redirect: true, callbackUrl: destination });
    return;
  } catch {
    // Fall back to a hard navigation if NextAuth sign-out fails.
  }

  window.location.replace(destination);
}
